import { useEffect, useMemo } from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import { Box, Button, Divider, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Action, useReceivedMessagesQuery } from '../../../../api/gql';
import { ActionIcon } from '../../../../components/invoice/ScheduledActionIcon';
import { renderCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateTimeGridColDef } from '../../../../components/table/utils/columnHeader';
import { formatDateWithTime } from '../../../../utils/format';
import { notify } from '../../../../utils/notification';

export const ReceivedMessagesTable = () => {
  const { t } = useTranslation('page-admin-received-messages');
  const { isLoading, data, error } = useReceivedMessagesQuery();

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({ message: t('query.error') });
    }
  }, [error, t]);

  const rows = useMemo(
    () =>
      data?.receivedMessages
        .sort((a, b) => {
          if (a.receivedAt < b.receivedAt) return -1;
          return 1;
        })
        .map((message) => {
          return {
            id: message.id,
            receivedAt: message.receivedAt,
            text: message.text,
            from: message.from,
            kind: message.kind,
            relevantScheduledActions: message.relevantScheduledActions,
          };
        }) ?? [],
    [data?.receivedMessages],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'receivedAt',
        headerName: t('table.receivedAt') as string,
        minWidth: 150,
        maxWidth: 180,
        flex: 1,
        ...dateTimeGridColDef,
      },
      {
        field: 'kind',
        headerName: t('table.kind') as string,
        flex: 1,
        minWidth: 130,
        maxWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'from',
        headerName: t('table.from') as string,
        flex: 1,
        minWidth: 150,
        maxWidth: 180,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'text',
        headerName: t('table.text') as string,
        flex: 1,
        minWidth: 200,
        maxWidth: 300,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'relevantScheduledActions',
        headerName: t('table.relevantScheduledActions') as string,
        disableColumnMenu: true,
        sortable: false,
        flex: 1,
        minWidth: 300,
        renderCell: ({ value }) => <RelevantScheduledActions actions={value} />,
      },
    ],
    [t],
  );

  return (
    <Table
      id={'admin-scheduled-actions'}
      loading={isLoading}
      autoHeight={true}
      getRowHeight={() => 'auto'}
      rows={rows}
      columns={columns}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
    />
  );
};

type RelevantScheduledActionsProps = {
  actions: {
    id: string;
    customerName: string;
    vendorName: string;
    invoiceId: string;
    invoiceNumber: string;
    action: Action;
    doneAt: string;
  }[];
};
const RelevantScheduledActions = (props: RelevantScheduledActionsProps) => {
  const getDisputeName = (action: RelevantScheduledActionsProps['actions'][number]) => {
    return `${action.customerName} x ${action.vendorName}`;
  };

  const disputesByInvoice = props.actions.reduce(
    (acc, action) => {
      if (!acc[action.invoiceId]) {
        acc[action.invoiceId] = {
          dispute: getDisputeName(action),
          invoiceNumber: action.invoiceNumber,
          relevantActions: [],
        };
      }
      acc[action.invoiceId].relevantActions.push({
        id: action.id,
        action: action.action,
        doneAt: action.doneAt,
      });

      return acc;
    },
    {} as Record<
      string,
      { dispute: string; invoiceNumber: string; relevantActions: { id: string; action: Action; doneAt: string }[] }
    >,
  );

  return (
    <Box py={1}>
      {Object.entries(disputesByInvoice).map(([invoiceId, disputeObj]) => {
        return (
          <Box key={invoiceId}>
            <Box display={'flex'} flexWrap={'wrap'} alignItems={'center'} gap={1}>
              <Typography variant={'body2'} fontWeight={'bold'}>
                {disputeObj.dispute} -
              </Typography>
              <Button
                size={'small'}
                endIcon={<DescriptionIcon />}
                component={Link}
                to={`/admin/invoice/${invoiceId}`}
                sx={{ p: 0, px: 1 }}
              >
                {disputeObj.invoiceNumber}
              </Button>
            </Box>
            <Divider />
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} pl={4}>
              {disputeObj.relevantActions.map((action) => (
                <Box key={action.id}>
                  -
                  <Button
                    size={'small'}
                    startIcon={<ActionIcon action={action.action} />}
                    component={Link}
                    to={`/admin/scheduled-action/${action.id}`}
                    sx={{ pl: 1 }}
                  >
                    {formatDateWithTime(new Date(action.doneAt))}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
